import isInstalledApplication from '@Application/ApplicationSettings/ApplicationRefresh/isInstalledApplication';
import refreshApplication from '@Application/ApplicationSettings/ApplicationRefresh/refreshApplication';
import signalRefreshArmed from '@Application/ApplicationSettings/ApplicationRefresh/signalRefreshArmed';
import captureInProgressSignal from '@Guidelines/Shell/Signals/captureInProgressSignal';
import type { PullBlockReason } from '@Guidelines/Shell/PullToRefresh/Hooks/usePullToRefreshGesture';
import usePullToRefreshGesture from '@Guidelines/Shell/PullToRefresh/Hooks/usePullToRefreshGesture';
import PullToRefreshIndicator from '@Guidelines/Shell/PullToRefresh/PullToRefreshIndicator';
import useConnectivity from '@UI/Providers/PortsProvider/Hooks/Connectivity/useConnectivity';
import { css, Global } from '@emotion/react';
import { useSignalValue } from '@verclam/pulsar';
import * as React from 'react';

const overscrollStyles = css`
	html,
	body {
		overscroll-behavior-y: contain;
	}
`;

/**
 * Pull-to-refresh for the installed application, where the browser's own gesture is absent.
 * Inert outside it: no listener, nothing rendered. Blocked while a capture is in progress or the device is offline.
 */
const PullToRefresh: React.FC = () => {
	const isEnabled = React.useMemo(() => isInstalledApplication(), []);
	const isCaptureInProgress = useSignalValue(captureInProgressSignal);
	const isOnline = useConnectivity();

	const blockReason = React.useMemo((): PullBlockReason | null => {
		if (isCaptureInProgress) {
			return 'capture';
		}

		return isOnline ? null : 'offline';
	}, [isCaptureInProgress, isOnline]);

	const handleRefresh = React.useCallback((): void => {
		void refreshApplication();
	}, []);

	const {
		phase,
		distance,
		blockReason: activeBlockReason,
	} = usePullToRefreshGesture({
		isEnabled,
		blockReason,
		onRefresh: handleRefresh,
	});

	React.useEffect(() => {
		if (phase === 'armed') {
			signalRefreshArmed();
		}
	}, [phase]);

	if (!isEnabled) {
		return null;
	}

	return (
		<React.Fragment>
			<Global styles={overscrollStyles} />
			<PullToRefreshIndicator phase={phase} distance={distance} blockReason={activeBlockReason} />
		</React.Fragment>
	);
};

export default PullToRefresh;
